// ============================================================================
// CneSidebar — sticky left rail navigation for the Clinical Notes Engine.
//
// Solves the "long-scroll fatigue" without retreating to tabs (which would
// hide context). Every section is listed with:
//   • a completion dot — filled (emerald) when the section has content,
//     hollow (slate) when empty
//   • a click-to-scroll behaviour with smooth scroll + a small top offset
//     so the section header isn't flush against the viewport top
//   • an "active" highlight derived from IntersectionObserver — the section
//     currently in the top third of the viewport is bolded
//
// SECTIONS is the single source of truth for both the rail order and the
// `id` attributes the editor body must expose. If you add a new section to
// EncounterEditor, add a matching entry here (the rail won't pick it up
// otherwise) and give the Section element the matching `id` prop.
//
// `note`, `encounter`, and `dxBuckets` are passed from EncounterEditor so
// completion checks can read the live values without re-fetching. Each
// `check` function should return truthy when the section is "filled".
// ============================================================================

import { useEffect, useMemo, useState } from 'react'

const NON_EMPTY = (s) => Boolean((s ?? '').toString().trim())

export const SECTIONS = [
  {
    id: 'sec-chief-complaint', short: 'Chief complaint', letter: 'A',
    check: (_n, enc) => NON_EMPTY(enc?.chief_complaint),
  },
  {
    id: 'sec-hpi', short: 'History of presenting illness', letter: 'B',
    check: (n) => NON_EMPTY(n?.history_of_presenting_illness),
  },
  {
    id: 'sec-ample', short: 'AMPLE history', letter: 'C',
    check: (n) => (
      NON_EMPTY(n?.ample_allergies) ||
      NON_EMPTY(n?.ample_medications) ||
      NON_EMPTY(n?.ample_past_medical_history) ||
      NON_EMPTY(n?.ample_last_meal) ||
      NON_EMPTY(n?.ample_events)
    ),
  },
  {
    id: 'sec-ros', short: 'Review of systems', letter: 'D',
    check: (n) => NON_EMPTY(n?.review_of_systems),
  },
  {
    id: 'sec-vitals', short: 'Vital signs', letter: 'E',
    check: (_n, enc) => {
      const v = enc?.structured?.vitals || {}
      return Object.values(v).some((x) => x != null && x !== '')
    },
  },
  {
    id: 'sec-bedside', short: 'Bedside tests', letter: 'F',
    // "Filled" when at least one bedside test record exists. Per-row
    // result content isn't required for completion — picking the test
    // (e.g. "ECG12L performed") is itself a clinical action.
    check: (_n, enc) => (enc?.bedside_test_records || []).length > 0,
  },
  {
    id: 'sec-exam', short: 'Examination', letter: 'G',
    check: (n) => NON_EMPTY(n?.physical_examination),
  },
  {
    id: 'sec-investigations', short: 'Investigations (Lab + Imaging)', letter: 'H·I',
    check: (_n, enc) => (
      (enc?.lab_orders || []).length > 0 ||
      (enc?.imaging_orders || []).length > 0
    ),
  },
  {
    id: 'sec-provisional', short: 'Provisional diagnosis', letter: 'J',
    check: (_n, _e, dx) => (dx?.provisional || []).length > 0,
  },
  {
    id: 'sec-differential', short: 'Differential diagnoses', letter: 'K',
    check: (_n, _e, dx) => (dx?.differential || []).length > 0,
  },
  {
    id: 'sec-empirical', short: 'Empirical treatment', letter: 'L',
    check: (n) => NON_EMPTY(n?.empirical_treatment),
  },
  {
    id: 'sec-final', short: 'Final diagnoses', letter: 'M',
    check: (_n, _e, dx) => (dx?.final || []).length > 0,
  },
  {
    id: 'sec-plan', short: 'Treatment plan', letter: 'N',
    check: (n) => NON_EMPTY(n?.treatment_plan),
  },
  {
    id: 'sec-prescriptions', short: 'Prescriptions', letter: 'O',
    check: (_n, enc) => (enc?.prescriptions || []).length > 0,
  },
  {
    id: 'sec-referrals', short: 'Referrals', letter: 'P',
    check: (_n, enc) => (enc?.referrals || []).length > 0,
  },
  {
    id: 'sec-ambulance', short: 'Ambulance request', letter: 'Q',
    check: (_n, enc) => (enc?.ambulance_requests || []).length > 0,
  },
  {
    id: 'sec-followup', short: 'Follow-up plan', letter: 'R',
    check: (n) => NON_EMPTY(n?.follow_up_plan),
  },
]

/**
 * Scroll an in-page section into view with a top offset so the section
 * heading isn't pinned to the viewport's top edge.
 *
 * Uses `Element.scrollIntoView` (not `window.scrollTo`) so the browser
 * walks up the DOM to find the actual scroll ancestor and scrolls THAT
 * — works whether the app's scroll container is HTML/window (typical)
 * OR nested (e.g. `<body>` when `html, body, #root { height: 100% }`
 * combined with an overflow rule promotes body to the scroll container,
 * see task #55 and web/src/styles/index.css).
 *
 * The 96px sticky-header offset is expressed via CSS `scroll-mt-24` on
 * the target `<Section>` (see AutosaveField.jsx) rather than a manual
 * `rect.top + scrollY - offset` computation. Browsers honour
 * scroll-margin-top when scrolling to the element, so the section title
 * lands ~6rem below the viewport top consistently across scroll
 * containers.
 *
 * History: the earlier implementation used
 *   window.scrollTo({ top: rect.top + window.scrollY - 96, behavior: 'smooth' })
 * which silently no-ops when body (not window) is the scroll container
 * — a real regression on prod that was invisible on UAT. Task #55
 * confirmed via DevTools that `window.scrollTo` was called with a
 * correct top value but produced no visible scroll on prod because
 * `document.scrollingElement` (HTML) had no scroll room (scrollHeight
 * == clientHeight); the scroll needed to happen on body. `scrollIntoView`
 * sidesteps the entire scroll-container-identification problem.
 */
function scrollToSection(id) {
  const el = document.getElementById(id)
  if (!el) return
  el.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

export default function CneSidebar({ note, encounter, dxBuckets }) {
  const [activeId, setActiveId] = useState(SECTIONS[0]?.id || null)

  // Completion map — derived once per render from the current note /
  // encounter / dxBuckets so the dots update live as the doctor types.
  const filled = useMemo(() => {
    const m = {}
    for (const s of SECTIONS) {
      try { m[s.id] = Boolean(s.check(note, encounter, dxBuckets)) }
      catch { m[s.id] = false }
    }
    return m
  }, [note, encounter, dxBuckets])

  const completedCount = useMemo(
    () => SECTIONS.reduce((acc, s) => acc + (filled[s.id] ? 1 : 0), 0),
    [filled],
  )

  // IntersectionObserver — pick the section currently nearest the top
  // third of the viewport as the "active" one. rootMargin keeps the
  // active highlight predictable: a section becomes active once its top
  // crosses 20% down from the viewport top, and stops being active once
  // its top scrolls past 70% down (i.e. it's mostly off-screen above).
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(
      (entries) => {
        // Among currently-intersecting sections, prefer the topmost one.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible.length > 0) {
          setActiveId(visible[0].target.id)
        }
      },
      { rootMargin: '-20% 0px -70% 0px', threshold: 0 },
    )
    // Observe with a tiny defer so the DOM has rendered the sections
    // (in particular when the editor body remounts on encounter switch).
    const handle = setTimeout(() => {
      for (const s of SECTIONS) {
        const el = document.getElementById(s.id)
        if (el) observer.observe(el)
      }
    }, 0)
    return () => {
      clearTimeout(handle)
      observer.disconnect()
    }
    // Re-bind when the encounter changes — EncounterEditor keys its body
    // on encounterId, so the section DOM nodes get replaced and the
    // observer needs to re-attach to the fresh ones.
  }, [encounter?.id])

  return (
    <aside
      // sticky top offset matches the desktop header height; on narrow
      // viewports the sidebar collapses to a horizontal scroll strip.
      className="
        lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-2rem)]
        lg:overflow-y-auto lg:w-56 lg:shrink-0
        bg-white lg:bg-slate-50/70 lg:border lg:border-slate-200 lg:rounded-xl
        lg:p-3
      "
      aria-label="Encounter sections"
    >
      {/* Header — section progress */}
      <div className="hidden lg:flex items-baseline justify-between mb-2 px-1">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
          Sections
        </span>
        <span className="text-[10px] font-mono text-slate-500">
          {completedCount}/{SECTIONS.length}
        </span>
      </div>

      {/* Desktop vertical list */}
      <nav className="hidden lg:block">
        <ul className="space-y-0.5">
          {SECTIONS.map((s) => {
            const isActive = activeId === s.id
            const isFilled = filled[s.id]
            return (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => scrollToSection(s.id)}
                  className={
                    'w-full flex items-center gap-2 text-left text-xs px-2 py-1.5 rounded-md transition ' +
                    (isActive
                      ? 'bg-wantok-100 text-wantok-900 font-semibold'
                      : 'text-slate-600 hover:bg-white hover:text-slate-900')
                  }
                  aria-current={isActive ? 'true' : undefined}
                >
                  <Dot filled={isFilled} active={isActive} />
                  <span className="font-mono text-[10px] text-slate-400 w-6 shrink-0">
                    {s.letter}
                  </span>
                  <span className="truncate">{s.short}</span>
                </button>
              </li>
            )
          })}
        </ul>
      </nav>

      {/* Mobile / narrow — horizontal scroll chip strip pinned to top */}
      <nav
        className="lg:hidden -mx-2 px-2 pb-2 overflow-x-auto sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-slate-200"
        aria-label="Encounter sections (compact)"
      >
        <ul className="flex gap-1.5 py-2 whitespace-nowrap">
          {SECTIONS.map((s) => {
            const isActive = activeId === s.id
            const isFilled = filled[s.id]
            return (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => scrollToSection(s.id)}
                  className={
                    'inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border transition ' +
                    (isActive
                      ? 'bg-wantok-700 text-white border-wantok-700 font-semibold'
                      : isFilled
                        ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                        : 'bg-white text-slate-600 border-slate-200')
                  }
                >
                  <span className="font-mono text-[10px] opacity-80">{s.letter}</span>
                  <span>{s.short}</span>
                </button>
              </li>
            )
          })}
        </ul>
      </nav>
    </aside>
  )
}

/**
 * Section completion dot. Emerald-filled when the section has content,
 * hollow slate when empty. Subtly larger when active so the rail's
 * current row has obvious visual weight.
 */
function Dot({ filled, active }) {
  const size = active ? 'w-2.5 h-2.5' : 'w-2 h-2'
  const cls = filled
    ? 'bg-emerald-500 border-emerald-500'
    : 'bg-white border-slate-300'
  return (
    <span
      className={`inline-block rounded-full border ${size} ${cls} shrink-0`}
      aria-hidden="true"
    />
  )
}
